export type YesNoUnknown = 'yes' | 'no' | 'unknown';

export type MedicationCategory =
  | 'stimulant'
  | 'glp1'
  | 'statin'
  | 'antihypertensive'
  | 'antidepressant'
  | 'hormone_therapy'
  | 'contraceptive'
  | 'sleep_medication'
  | 'other';

export type MedicationStatus = 'current' | 'as_needed' | 'stopped';
export type MedicationTiming = 'morning' | 'evening' | 'multiple_times_daily' | 'variable';
export type MedicationRoute = 'oral' | 'injection' | 'topical' | 'inhaled' | 'other';
export type MedicationPrescribedBy = 'gp' | 'specialist' | 'private_clinician' | 'self_directed_or_other';
export type MemberConfidence = 'exact' | 'approximate';
export type EntrySource = 'member_entered' | 'clinician_entered' | 'imported';

export type SupplementCategory =
  | 'omega_3'
  | 'vitamin_d'
  | 'magnesium'
  | 'creatine'
  | 'probiotic'
  | 'iron'
  | 'b12'
  | 'multivitamin'
  | 'protein_powder'
  | 'other';

export type SupplementStatus = 'current' | 'occasional' | 'stopped';
export type SupplementPurpose = 'general_health' | 'sleep' | 'performance' | 'deficiency' | 'clinician_advised' | 'other';

export type FrequencyBand = 'never' | 'monthly' | 'weekly' | 'several_times_weekly';
export type PatternLevel = 'low' | 'moderate' | 'high';
export type VegetarianOrVeganPattern = 'none' | 'vegetarian' | 'vegan' | 'pescatarian';
export type AlcoholPattern = 'none' | 'occasional' | 'weekly' | 'frequent';
export type NicotineUse = 'none' | 'former' | 'occasional' | 'current';
export type CannabisUse = 'none' | 'occasional' | 'current';
export type TrainingFrequency = 'none' | 'one_to_two_per_week' | 'three_to_four_per_week' | 'five_plus_per_week';
export type SleepConcern = 'none' | 'occasional' | 'ongoing';
export type InputsUpdatedBy = 'member' | 'clinician';

export interface MedicationEntry {
  id: string;
  name: string;
  genericName?: string | null;
  brandName?: string | null;
  category: MedicationCategory;
  indication?: string | null;
  status: MedicationStatus;
  doseValue?: string | null;
  doseUnit?: string | null;
  frequency?: string | null;
  timing?: MedicationTiming | null;
  route?: MedicationRoute | null;
  startDate?: string | null;
  endDate?: string | null;
  prescribedBy?: MedicationPrescribedBy | null;
  /**
   * The named prescribing clinician.
   *
   * Medicines §5.1 requires the history to cover doses "together with the
   * prescribing clinician". `prescribedBy` above is a category — gp, specialist,
   * private_clinician, self_directed_or_other — which answers what kind of
   * prescriber, not which one. Without a name the policy cannot be satisfied in
   * full, so this is the field that closes it.
   *
   * Optional and free-text: members frequently will not know, and forcing a value
   * would produce confident-looking guesses. Empty is a truthful answer here.
   */
  prescriberName?: string | null;
  memberConfidence?: MemberConfidence | null;
  notes?: string | null;
  source?: EntrySource | null;
  updatedAt?: unknown;
}

/**
 * Evidence that the medication history was reviewed, and when.
 *
 * Medicines §5.1 says the history is reviewed "at each consultation". The profile
 * already carried `updatedAt`/`updatedBy`, but those move only when something
 * changes — a review that correctly finds nothing to change left no trace at all,
 * so there was no way to show it happened. This stamp records the review itself.
 *
 * Written by recordConsultationAttestation, never by the member app.
 */
export interface MedicationReviewStamp {
  lastReviewedAt: unknown;
  reviewedByUid: string;
  reviewedByName: string;
  /** The consultation the review happened in, for the audit trail. */
  consultationId: string;
  /** True where the clinician amended the history during the review. */
  changesMade: boolean;
}

export interface SupplementEntry {
  id: string;
  name: string;
  category: SupplementCategory;
  status: SupplementStatus;
  doseValue?: string | null;
  doseUnit?: string | null;
  frequency?: string | null;
  timing?: string | null;
  startDate?: string | null;
  purpose?: SupplementPurpose | null;
  memberConfidence?: MemberConfidence | null;
  notes?: string | null;
  updatedAt?: unknown;
}

export interface DietPatterns {
  oilyFishFrequency?: FrequencyBand | null;
  redMeatFrequency?: FrequencyBand | null;
  processedFoodPattern?: PatternLevel | null;
  vegetarianOrVegan?: VegetarianOrVeganPattern | null;
  proteinFocus?: PatternLevel | null;
  alcoholPattern?: AlcoholPattern | null;
  caffeinePattern?: PatternLevel | null;
  dairyPattern?: 'regular' | 'limited' | 'none' | null;
  fiberFocus?: PatternLevel | null;
  notes?: string | null;
}

export interface RelevantExposures {
  nicotineUse?: NicotineUse | null;
  cannabisUse?: CannabisUse | null;
  trainingFrequency?: TrainingFrequency | null;
  sleepConcern?: SleepConcern | null;
  notes?: string | null;
}

export interface MyInputsCurrentProfile {
  medications: MedicationEntry[];
  supplements: SupplementEntry[];
  dietPatterns: DietPatterns;
  exposures: RelevantExposures;
  updatedAt?: unknown;
  updatedBy?: InputsUpdatedBy;
  /** Medicines §5.1 — see MedicationReviewStamp. Absent until first reviewed. */
  medicationReview?: MedicationReviewStamp | null;
}

export interface LegacyMyInputsFallback {
  medications?: string[];
}

export const EMPTY_MY_INPUTS_PROFILE: MyInputsCurrentProfile = {
  medications: [],
  supplements: [],
  dietPatterns: {},
  exposures: {},
};

export interface MyInputsOverview {
  currentMedicationCount: number;
  currentSupplementCount: number;
  dietSummary: string;
  updatedLabel: string;
}
